import omit from 'lodash/omit';
import pick from 'lodash/pick';
import transform from 'lodash/transform';
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {HISTORICAL_PROFILE_PROPERTY_PREFIX, PROPERTIES} from 'common/report/constants';
import {ToggleOption, ToggleTheme} from 'common/components/toggle/types';
import {assertUnreachable} from 'common/util/typescript';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {PropertyKeyFilter, ViewFormat} from 'common/report/events/types';
import {formatEventProperty} from 'common/report/events/utils';

import {TrackEvents} from '../tracking';
import {template} from './template';
import './index.styl';
import {ExpandedRowJsxProps, ExpandedRowState, Row} from './types';

function getReservedProperties(properties: Record<string, any>): string[] {
  return Object.keys(properties).filter(
    (propName) =>
      Object.prototype.hasOwnProperty.call(PROPERTIES, propName) ||
      propName.startsWith(HISTORICAL_PROFILE_PROPERTY_PREFIX),
  );
}

export class ExpandedRow extends Component<ExpandedRowState, ExpandedRowJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<ExpandedRowJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      toggletheme: {type: `string`, default: ToggleTheme.SegmentedController, enum: Object.values(ToggleTheme)},
      toggleoptions: {type: `json`, default: null},
      'property-key-filter': {
        type: `string`,
        enum: Object.values(PropertyKeyFilter),
        default: PropertyKeyFilter.AllProperties,
      },
      'hide-property-key-filter': {type: `boolean`},
      'view-format': {type: `string`, default: ViewFormat.Table, enum: Object.values(ViewFormat)},
      'allow-pinning': {type: `boolean`},
    };
  }

  set row(row: Row) {
    this.update({
      row,
    });
  }

  get config() {
    return {
      template,
      defaultState: {
        row: {},
        pinnedProperties: [],
      },
      helpers: {
        onChangeViewFormat: (ev: CustomEvent) => {
          const on = ev.detail.on;
          let nextFormat = ViewFormat.Table;
          if (on) {
            nextFormat = ViewFormat.Json;
          }
          this.dispatchEvent(
            new CustomEvent(`viewFormatChange`, {
              detail: {viewFormat: nextFormat},
            }),
          );
          mixpanel.track(TrackEvents.ChangeViewFormat, {
            'View format': nextFormat,
          });
        },
        getPropertyKeyFilter: (): ToggleOption[] => {
          if (this.attr(`toggleoptions`)) {
            return this.attr(`toggleoptions`);
          }
          return Object.values(PropertyKeyFilter).map((value) => ({
            label: value,
            value,
          }));
        },
        onSelectPropertyKeyFilter: (ev: CustomEvent) => {
          const selected = ev.detail.selected;
          this.dispatchEvent(
            new CustomEvent(`propertyKeyFilterChange`, {
              detail: {propertyKeyFilter: selected},
            }),
          );
          mixpanel.track(TrackEvents.TogglePropertyKeyFilter, {
            'View format': this.attr(`view-format`),
            'Selected filter': selected,
          });
        },
        onPinClick: (key: string) => {
          let pins = this.state.pinnedProperties.slice(0);
          if (pins.includes(key)) {
            pins = pins.filter((k: string) => k !== key);
          } else {
            pins.unshift(key);
          }
          this.dispatchEvent(new CustomEvent(`pinsupdated`, {detail: {pins}}));
        },
        isPinned: (key: string) => this.state.pinnedProperties.includes(key),
        getPinnedProperties: () => this.state.pinnedProperties,
        isJsonView: () => {
          return this.isJsonView();
        },
        getFilteredEvent: () => {
          const selected: PropertyKeyFilter = this.attr(`property-key-filter`);
          const row = this.state.row;
          let properties = row.properties;
          switch (selected) {
            case PropertyKeyFilter.AllProperties:
              break;
            case PropertyKeyFilter.YourProperties:
              properties = omit(properties, getReservedProperties(properties));
              break;
            case PropertyKeyFilter.MixpanelProperties:
              properties = pick(properties, getReservedProperties(properties));
              break;
            default:
              return assertUnreachable(selected);
          }

          properties = this.getRenamedProperties(properties);

          return {event: row.event, properties};
        },
      },
    };
  }

  set pinnedProperties(pinnedProperties: any) {
    this.update({
      pinnedProperties,
    });
  }
  private isJsonView() {
    return this.attr(`view-format`) === ViewFormat.Json;
  }

  private getRenamedProperties(properties) {
    return transform(properties, (r, v: string, k: string) => {
      if (this.isJsonView()) {
        const type = eventPropertyDefinitionsStore.value[k]?.type;
        if (type === `object` || type === `list`) {
          try {
            v = JSON.parse(v);
          } catch (err) {
            // pass
          }
        }

        r[k] = v;
      } else {
        const {formattedName, formattedValue, href, raw} = formatEventProperty(k, v, {
          relativeTime: false,
        });
        r[formattedName] = {formattedValue, href, raw};
      }
    });
  }
}

export const ExpandedRowEl = defineAndMakeJsxFactory<ExpandedRowJsxProps>(`expanded-row`, ExpandedRow);
export const ExpandedRowReact = panel2react<ExpandedRowJsxProps>(`expanded-row`, ExpandedRow);
